import type { TFunction } from 'i18next';
import type { Initiative, KPIOverviewData } from '../../api/initiative-client/initiative-client.type';
import { Filters } from './initiative-list/filters/filters';
import { MainSection } from './initiative-list/main-section/main-section';
import { Overview } from './initiative-list/overview/overview';
import { SideSection } from './initiative-list/side-section/side-section';
import type { StrategyLevelConfig } from './strategy-config.type';
import { useStrategyData } from './strategy.hooks';

/**
 * Build the Initiatives strategy level configuration against the caller's
 * translation function so org-level text_config overrides apply.
 *
 * @param {TFunction} t - Translation function from `useTranslation()`
 * @returns {StrategyLevelConfig<Initiative, KPIOverviewData>} Strategy-level config bound to `t`
 */
export const getInitiativesConfig = (t: TFunction): StrategyLevelConfig<Initiative, KPIOverviewData> => ({
  level: 'initiatives',
  tabId: 'initiatives',

  labels: {
    pageTitle: 'Strategy - Bloomfilter',
    overviewTitle: t('strategy.overview.title'),
    filtersTitle: t('common.filters'),
    emptyLabel: t('strategy.initiatives.no_initiatives'),
    byStatusTitle: t('strategy.overview.by_status'),
    byStateTitle: t('strategy.overview.by_state'),
    searchPlaceholder: t('strategy.initiatives.search_placeholder'),
  },

  features: {
    hasTreeMapView: true,
    hasCreatePresentation: true,
    hasExport: true,
    hasSearch: true,
    hasAddNew: true,
    hasFilters: true,
    hasSidePanel: true,
  },

  hooks: {
    useItems: () => {
      const { initiatives, initiativesQuery } = useStrategyData();
      return { items: initiatives, query: initiativesQuery };
    },
    useKPIData: () => {
      const { kpiOverviewData, kpiOverviewDataQuery } = useStrategyData();
      return { data: kpiOverviewData, query: kpiOverviewDataQuery };
    },
  },

  components: {
    Filters,
    Overview,
    MainSection,
    SideSection,
  },
});
